import React, { useState, useEffect } from "react";
import {
  Package, PlusCircle, RefreshCw, ShieldAlert,
  CheckCircle, XCircle, ChevronDown, Tag, ClipboardList
} from "lucide-react";
import { saveMaterial, getOrders, getInventory } from "../../api/tagApi";
import { materialImageUrl } from "../../utils/materialCatalog";
import { SHAPES } from "../../config/inventory";

// Simple passcode gate — move to env var for production
const ADMIN_PASSCODE = import.meta.env.VITE_ADMIN_PASSCODE || "lasertags-admin";

const STATUS_COLORS = {
  pending:   "bg-yellow-500/20 text-yellow-400",
  paid:      "bg-green-500/20 text-green-400",
  shipped:   "bg-blue-500/20 text-blue-400",
  completed: "bg-emerald-500/20 text-emerald-400",
  cancelled: "bg-red-500/20 text-red-400",
};

const BLANK_MATERIAL = {
  shape: "bone",
  type: "",
  colorHex: "#ffffff",
  imageurl: "",
  instockcount: 10,
};

export default function AdminPanel() {
  const [authed, setAuthed]           = useState(false);
  const [passcode, setPasscode]       = useState("");
  const [passError, setPassError]     = useState(false);

  const [tab, setTab]                 = useState("inventory"); // "inventory" | "orders"
  const [inventory, setInventory]     = useState([]);
  const [orders, setOrders]           = useState([]);
  const [loading, setLoading]         = useState(false);

  const [form, setForm]               = useState(BLANK_MATERIAL);
  const [saving, setSaving]           = useState(false);
  const [saveResult, setSaveResult]   = useState(null); // "ok" | "err"

  // ── Auth ──────────────────────────────────────────────
  const handleLogin = (e) => {
    e.preventDefault();
    if (passcode === ADMIN_PASSCODE) {
      setAuthed(true);
    } else {
      setPassError(true);
      setTimeout(() => setPassError(false), 2000);
    }
  };

  // ── Data fetch ────────────────────────────────────────
  const fetchInventory = async () => {
    setLoading(true);
    const data = await getInventory();
    setInventory(data || []);
    setLoading(false);
  };

  const fetchOrders = async () => {
    setLoading(true);
    const data = await getOrders();
    setOrders(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => {
    if (!authed) return;
    if (tab === "inventory") fetchInventory();
    else fetchOrders();
  }, [authed, tab]);

  // ── Add material ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveResult(null);
    const result = await saveMaterial({
      shape:         form.shape,
      type:          form.type.trim(),
      colorhex:      form.colorHex,
      imageurl:      form.imageurl.trim() || null,
      instockcount:  Number(form.instockcount),
      in_back_order: false,
    });
    setSaving(false);
    if (result) {
      setSaveResult("ok");
      setForm(BLANK_MATERIAL);
      fetchInventory();
    } else {
      setSaveResult("err");
    }
    setTimeout(() => setSaveResult(null), 3000);
  };

  // ── Passcode screen ───────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-charcoal-900 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-charcoal-800 rounded-2xl p-8 border border-charcoal-700 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <ShieldAlert className="w-7 h-7 text-coral-500" />
            <h1 className="text-xl font-bold text-white">Admin Access</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className={`w-full px-4 py-3 bg-charcoal-900 border-2 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-coral-500 transition-colors ${
                passError ? "border-red-500 animate-pulse" : "border-charcoal-600"
              }`}
              autoFocus
            />
            {passError && (
              <p className="text-red-400 text-sm flex items-center gap-1">
                <XCircle className="w-4 h-4" /> Incorrect passcode
              </p>
            )}
            <button
              type="submit"
              className="w-full py-3 bg-coral-500 hover:bg-coral-600 text-white font-bold rounded-lg transition-colors"
            >
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin UI ──────────────────────────────────────────
  return (
    <div className="min-h-screen bg-charcoal-900 text-white">
      {/* Header */}
      <header className="bg-charcoal-800 border-b border-charcoal-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tag className="w-6 h-6 text-coral-500" />
          <span className="text-lg font-bold">LaserTags Admin</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("inventory")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === "inventory"
                ? "bg-coral-500 text-white"
                : "text-gray-400 hover:text-white hover:bg-charcoal-700"
            }`}
          >
            <Package className="w-4 h-4" /> Inventory
          </button>
          <button
            onClick={() => setTab("orders")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === "orders"
                ? "bg-coral-500 text-white"
                : "text-gray-400 hover:text-white hover:bg-charcoal-700"
            }`}
          >
            <ClipboardList className="w-4 h-4" /> Orders
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">

        {/* ── INVENTORY TAB ── */}
        {tab === "inventory" && (
          <>
            {/* Add Material Form */}
            <div className="bg-charcoal-800 rounded-2xl p-6 border border-charcoal-700">
              <h2 className="text-lg font-bold mb-5 flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-coral-500" />
                Add Material to Inventory
              </h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Shape */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Shape</label>
                  <div className="relative">
                    <select
                      value={form.shape}
                      onChange={(e) => setForm((f) => ({ ...f, shape: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-charcoal-900 border-2 border-charcoal-600 rounded-lg text-white appearance-none focus:outline-none focus:border-coral-500 transition-colors"
                    >
                      {SHAPES.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.icon} {s.displayName}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                  </div>
                </div>

                {/* Color / Type */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Color Name</label>
                  <input
                    type="text"
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    placeholder="e.g. Green, Dark Blue"
                    required
                    className="w-full px-4 py-2.5 bg-charcoal-900 border-2 border-charcoal-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-coral-500 transition-colors"
                  />
                </div>

                {/* Hex */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Color (hex)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.colorHex}
                      onChange={(e) => setForm((f) => ({ ...f, colorHex: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border-2 border-charcoal-600 bg-transparent"
                    />
                    <input
                      type="text"
                      value={form.colorHex}
                      onChange={(e) => setForm((f) => ({ ...f, colorHex: e.target.value }))}
                      className="flex-1 px-4 py-2.5 bg-charcoal-900 border-2 border-charcoal-600 rounded-lg text-white font-mono focus:outline-none focus:border-coral-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Stock count */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Initial Stock Count</label>
                  <input
                    type="number"
                    min="0"
                    value={form.instockcount}
                    onChange={(e) => setForm((f) => ({ ...f, instockcount: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 bg-charcoal-900 border-2 border-charcoal-600 rounded-lg text-white focus:outline-none focus:border-coral-500 transition-colors"
                  />
                </div>

                {/* Image URL */}
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Image URL <span className="text-gray-600">(optional — add later when photos are ready)</span></label>
                  <div className="flex items-center gap-3">
                    <input
                      type="url"
                      value={form.imageurl}
                      onChange={(e) => setForm((f) => ({ ...f, imageurl: e.target.value }))}
                      placeholder="/materials/11-orange-bone.jpg"
                      className="flex-1 px-4 py-2.5 bg-charcoal-900 border-2 border-charcoal-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-coral-500 transition-colors"
                    />
                    {form.imageurl && (
                      <img
                        src={materialImageUrl(form.imageurl)}
                        alt="preview"
                        className="w-10 h-10 rounded object-cover border border-charcoal-600"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2 flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2.5 bg-coral-500 hover:bg-coral-600 disabled:opacity-50 text-white font-bold rounded-lg transition-colors flex items-center gap-2"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                    {saving ? "Saving…" : "Add to Inventory"}
                  </button>

                  {saveResult === "ok" && (
                    <span className="flex items-center gap-1 text-green-400 text-sm font-semibold">
                      <CheckCircle className="w-4 h-4" /> Saved to Postgres
                    </span>
                  )}
                  {saveResult === "err" && (
                    <span className="flex items-center gap-1 text-red-400 text-sm font-semibold">
                      <XCircle className="w-4 h-4" /> Save failed — check backend
                    </span>
                  )}
                </div>
              </form>
            </div>

            {/* Current Inventory Table */}
            <div className="bg-charcoal-800 rounded-2xl border border-charcoal-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-charcoal-700 flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2">
                  <Package className="w-4 h-4 text-coral-500" /> Current Inventory
                </h2>
                <button
                  onClick={fetchInventory}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-charcoal-900 text-gray-400 uppercase text-xs tracking-wider">
                    <tr>
                      <th className="px-6 py-3 text-left">ID</th>
                      <th className="px-6 py-3 text-left">Shape</th>
                      <th className="px-6 py-3 text-left">Color</th>
                      <th className="px-6 py-3 text-center">Image</th>
                      <th className="px-6 py-3 text-right">In Stock</th>
                      <th className="px-6 py-3 text-center">Back Order</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal-700">
                    {loading && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                          <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Loading…
                        </td>
                      </tr>
                    )}
                    {!loading && inventory.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                          No materials yet — add one above.
                        </td>
                      </tr>
                    )}
                    {!loading && inventory.map((mat) => (
                      <tr key={mat.id} className="hover:bg-charcoal-700/40 transition-colors">
                        <td className="px-6 py-3 text-gray-400 font-mono">{mat.id}</td>
                        <td className="px-6 py-3 capitalize">{mat.shape}</td>
                        <td className="px-6 py-3 flex items-center gap-2">
                          {mat.colorHex && (
                            <span
                              className="w-3 h-3 rounded-full inline-block border border-white/20"
                              style={{ backgroundColor: mat.colorHex }}
                            />
                          )}
                          {mat.type}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <span className={`font-bold ${mat.instockcount > 0 ? "text-green-400" : "text-red-400"}`}>
                            {mat.instockcount ?? "—"}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-center">
                          {mat.in_back_order
                            ? <span className="text-yellow-400 text-xs font-bold">YES</span>
                            : <span className="text-gray-600 text-xs">—</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── ORDERS TAB ── */}
        {tab === "orders" && (
          <div className="bg-charcoal-800 rounded-2xl border border-charcoal-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-charcoal-700 flex items-center justify-between">
              <h2 className="font-bold flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-coral-500" /> Recent Orders
              </h2>
              <button
                onClick={fetchOrders}
                className="text-gray-400 hover:text-white transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-charcoal-900 text-gray-400 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-6 py-3 text-left">Order ID</th>
                    <th className="px-6 py-3 text-left">Pet</th>
                    <th className="px-6 py-3 text-left">Material</th>
                    <th className="px-6 py-3 text-right">Amount</th>
                    <th className="px-6 py-3 text-center">Status</th>
                    <th className="px-6 py-3 text-left">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal-700">
                  {loading && (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />Loading…
                      </td>
                    </tr>
                  )}
                  {!loading && orders.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        No orders yet.
                      </td>
                    </tr>
                  )}
                  {!loading && orders.map((order) => (
                    <tr key={order.id} className="hover:bg-charcoal-700/40 transition-colors">
                      <td className="px-6 py-3 font-mono text-gray-400">#{order.id}</td>
                      <td className="px-6 py-3">
                        <div className="font-semibold">{order.petname || "—"}</div>
                        <div className="text-xs text-gray-500">{order.firstname} {order.lastname}</div>
                      </td>
                      <td className="px-6 py-3 capitalize">
                        {order.shape && order.type
                          ? `${order.shape} / ${order.type}`
                          : order.materialid ?? "—"}
                      </td>
                      <td className="px-6 py-3 text-right font-semibold">
                        {order.amount != null
                          ? `$${(order.amount / 100).toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="px-6 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${STATUS_COLORS[order.status] || "bg-gray-700 text-gray-300"}`}>
                          {order.status || "unknown"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-400 text-xs">
                        {order.created_at
                          ? new Date(order.created_at).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
